<template>
  <div class="w-full">
    <div class="flex w-full flex-col items-center pb-8 lg:flex-row">
      <h3 class="text-display-small">{{ feedbackTopic.title }}</h3>
      <div class="flex flex-row items-center gap-2 lg:ml-auto">
        <div v-if="data" class="flex flex-row">
          <BaseMemberAvatars class="bg-surface-container-lowest" :members="data.feedbackSenders" />
        </div>
        <BaseTextButton
          v-if="allSurveys.length > 0"
          color="outlined"
          :size="{ base: 'S', lg: 'M' }"
          shape="square"
          icon="keyboard_arrow_down"
          @click="openDialog"
        >
          {{ inputData.surveyIds.length }} Umfragen ausgewählt</BaseTextButton
        >
        <div
          v-if="!hasChanged && slideProperties.updatedAt"
          class="bg-surface-container text-title-small lg:text-title-medium text-on-surface-variant flex h-10 flex-row items-center rounded-full px-6 lg:h-14"
        >
          <BaseIcon class="mr-2">check_circle</BaseIcon>
          Abgeschlossen am {{ useDisplayDate(slideProperties.updatedAt, { dayName: 'none' }) }}
        </div>
        <BaseTextButton
          v-if="hasChanged && slideProperties.updatedAt"
          color="filled"
          :size="{ base: 'S', lg: 'M' }"
          icon="cloud_upload"
          @click="updateSlide"
          >Änderungen Speichern</BaseTextButton
        >
        <BaseTextButton
          v-if="!slideProperties.updatedAt"
          color="filled"
          :size="{ base: 'S', lg: 'M' }"
          icon="check_circle"
          @click="updateSlide"
          >Thema Abschliessen</BaseTextButton
        >
      </div>
    </div>

    <SurveySelectorDialog
      :subject="user"
      title="Umfragen auswählen"
      :model-value="inputData.surveyIds"
      :is-open="isOpen"
      :all-surveys="allSurveys"
      :topic-perspectives="perspectivesFromTopic(feedbackTopic)"
      @close-dialog="closeDialog"
      @update:model-value="selectSurveys"
    />

    <div v-if="allSurveys.length > 0">
      <LoadingWrapper :state="status">
        <PulseProfileAnalysis
          v-if="data"
          :questions="data.questions"
          :topic-perspectives="perspectivesFromTopic(feedbackTopic)"
        />
      </LoadingWrapper>
    </div>
    <div v-else class="mx-auto flex max-w-lg flex-col items-center justify-center p-4">
      <EmptyQuestions />
      <h3 class="text-headline-medium text-center">Du hast noch kein Feedback zu diesem Thema</h3>
      <p class="text-body-large text-on-surface-variant mt-2 text-center">
        Starte selbst eine Umfrage, gib eine Selbsteinschätzung ab oder warte auf Rückmeldungen von
        anderen.
      </p>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { watch } from 'vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import SurveySelectorDialog from '../../../core/components/survey-selector/SurveySelectorDialog.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { usePulseFeedbackTopicSlideAnalysis } from '../composable/use-pulse-feedback-topic-slide-analysis'
  import LoadingWrapper from '../../../core/components/loading/LoadingWrapper.vue'
  import type { FeedbackTopic } from '../../../core/models/feedback-topic'
  import BaseMemberAvatars from '../../../core/components/base/avatar/BaseMemberAvatars.vue'
  import { useDisplayDate } from '../../../core/composables/use-display-date'
  import BaseIcon from '../../../core/components/base/icon/BaseIcon.vue'
  import type { SurveySelectionPreview } from '@/modules/profile/composables/use-evaluation-profile'
  import EmptyQuestions from '@/core/components/analytics/question-list/EmptyQuestions.vue'
  import PulseProfileAnalysis from '@/core/components/analytics/PulseProfileAnalysis.vue'
  import { perspectivesFromTopic } from '@/core/models/perspective'
  import { useUserStore } from '@/stores/user.store'

  interface Props {
    feedbackTopic: FeedbackTopic
    allSurveys: ReadonlyArray<SurveySelectionPreview>
    slideProperties: {
      updatedAt: string | null
      surveyIds: Array<string>
    }
  }
  const props = defineProps<Props>()
  const { isOpen, closeDialog, openDialog } = useDialog()
  const { user } = useUserStore()
  const emit = defineEmits<{
    (e: 'dirty', value: boolean): void
  }>()

  const selectSurveys = (surveyIds: string[]) => {
    closeDialog()
    inputData.surveyIds = surveyIds
    reloadAnalysis(surveyIds)
  }

  const { data, status, reloadAnalysis, updateSlide, inputData, hasChanged } =
    usePulseFeedbackTopicSlideAnalysis(
      props.feedbackTopic.id,
      props.allSurveys.map((survey) => survey.id),
      props.slideProperties,
    )

  watch(hasChanged, (value) => {
    emit('dirty', value)
  })
</script>
